/**
 * "Anotaciones | Asistente" tabs in the right drawer. The annotations workspace keeps every element it
 * built (they move into the first tab panel unchanged); the Asistente panel is the second one.
 * WAI-ARIA tabs: arrow keys / Home / End move between tabs, the selected one is the only tab stop.
 */

export type DrawerTab = 'annotations' | 'assistant';

const TAB_LABELS: Record<DrawerTab, string> = { annotations: 'Anotaciones', assistant: 'Asistente Guru' };
const ORDER: DrawerTab[] = ['annotations', 'assistant'];

export interface DrawerTabs {
  select(tab: DrawerTab, focus?: boolean): void;
  current(): DrawerTab;
}

export function installTabs(drawer: HTMLElement, assistantPanel: HTMLElement, onSelect: (tab: DrawerTab) => void): DrawerTabs {
  const annotationsPanel = document.createElement('div');
  annotationsPanel.className = 'drawer-tabpanel annot-tabpanel';
  annotationsPanel.append(...Array.from(drawer.childNodes));
  assistantPanel.classList.add('drawer-tabpanel');
  const panels: Record<DrawerTab, HTMLElement> = { annotations: annotationsPanel, assistant: assistantPanel };

  const bar = document.createElement('div');
  bar.className = 'drawer-tabs';
  bar.setAttribute('role', 'tablist');
  bar.setAttribute('aria-label', 'Panel derecho');
  const tabs = {} as Record<DrawerTab, HTMLButtonElement>;
  for (const key of ORDER) {
    const tab = document.createElement('button');
    tab.type = 'button';
    tab.className = 'drawer-tab';
    tab.id = `drawer-tab-${key}`;
    tab.dataset.tab = key;
    tab.textContent = TAB_LABELS[key];
    tab.setAttribute('role', 'tab');
    const panel = panels[key];
    panel.id = `drawer-tabpanel-${key}`;
    panel.setAttribute('role', 'tabpanel');
    panel.setAttribute('aria-labelledby', tab.id);
    tab.setAttribute('aria-controls', panel.id);
    tabs[key] = tab;
    bar.appendChild(tab);
  }

  let selected: DrawerTab = 'annotations';
  const select = (tab: DrawerTab, focus = false) => {
    selected = tab;
    for (const key of ORDER) {
      const on = key === tab;
      tabs[key].setAttribute('aria-selected', String(on));
      tabs[key].tabIndex = on ? 0 : -1;
      tabs[key].classList.toggle('active', on);
      panels[key].hidden = !on;
    }
    drawer.classList.toggle('assistant-active', tab === 'assistant');
    if (focus) tabs[tab].focus();
    onSelect(tab);
  };

  bar.addEventListener('click', (event) => {
    const tab = (event.target as HTMLElement).closest<HTMLButtonElement>('.drawer-tab');
    if (tab?.dataset.tab) select(tab.dataset.tab as DrawerTab);
  });
  bar.addEventListener('keydown', (event) => {
    const at = ORDER.indexOf(selected);
    const next: Record<string, number> = { ArrowRight: at + 1, ArrowLeft: at - 1, Home: 0, End: ORDER.length - 1 };
    if (!(event.key in next)) return;
    // Handled here: the viewer's keyboard map (slice / series keys) skips defaultPrevented events.
    event.preventDefault();
    select(ORDER[(next[event.key] + ORDER.length) % ORDER.length], true);
  });

  drawer.classList.add('has-assistant');
  drawer.append(bar, annotationsPanel, assistantPanel);
  select('annotations');
  return { select, current: () => selected };
}
